// Dinamik API URL - mobil uyumlu
const getApiBaseUrl = () => {
  // Environment variable'dan al (production için)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Development ortamında localhost kullan
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:5000/api';
  }
  
  // Production ortamında window.location'dan al
  if (typeof window !== 'undefined') {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    
    // Aynı hostname'de API çalışıyor varsayımı
    return `${protocol}//${hostname}/api`;
  }
  
  // Fallback
  return 'http://localhost:5000/api';
};

const API_BASE_URL = getApiBaseUrl();

// Cache için basit bir cache sistemi
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika

class ApiService {
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    const token = localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      console.log('API Request:', url, config);
      const response = await fetch(url, config);
      
      // Response kontrolü
      if (!response.ok) {
        let errorMessage = 'API request failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      return data;
    } catch (error) {
      console.error('API Error:', {
        url,
        error: error.message,
        stack: error.stack
      });
      
      // Mobil cihazlar için daha detaylı hata mesajları
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.');
      }
      
      throw error;
    }
  }

  // Randevu servisleri
  async createAppointment(appointmentData) {
    return this.request('/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async getAppointments() {
    return this.request('/appointments');
  }

  async getAvailableSlots(date, teamMemberId, serviceIds, packageIds, totalDuration) {
    let queryParams = [];
    
    if (teamMemberId) {
      queryParams.push(`teamMemberId=${teamMemberId}`);
    }
    
    if (serviceIds && serviceIds.length > 0) {
      const serviceIdsParam = Array.isArray(serviceIds) ? serviceIds.join(',') : serviceIds;
      queryParams.push(`serviceIds=${serviceIdsParam}`);
    }
    
    if (packageIds && packageIds.length > 0) {
      const packageIdsParam = Array.isArray(packageIds) ? packageIds.join(',') : packageIds;
      queryParams.push(`packageIds=${packageIdsParam}`);
    }
    
    if (totalDuration && totalDuration > 0) {
      queryParams.push(`totalDuration=${totalDuration}`);
    }
    
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    return this.request(`/appointments/available-slots/${date}${queryString}`);
  }

  async getAvailableDates(teamMemberId, serviceIds, packageIds, totalDuration) {
    let queryParams = [];
    
    if (teamMemberId) {
      queryParams.push(`teamMemberId=${teamMemberId}`);
    }
    
    if (serviceIds && serviceIds.length > 0) {
      const serviceIdsParam = Array.isArray(serviceIds) ? serviceIds.join(',') : serviceIds;
      queryParams.push(`serviceIds=${serviceIdsParam}`);
    }
    
    if (packageIds && packageIds.length > 0) {
      const packageIdsParam = Array.isArray(packageIds) ? packageIds.join(',') : packageIds;
      queryParams.push(`packageIds=${packageIdsParam}`);
    }
    
    if (totalDuration && totalDuration > 0) {
      queryParams.push(`totalDuration=${totalDuration}`);
    }
    
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    return this.request(`/appointments/available-dates${queryString}`);
  }

  async checkAppointmentConflict(teamMemberId, date, time, serviceIds, excludeAppointmentId = undefined) {
    const params = new URLSearchParams({
      teamMemberId: teamMemberId.toString(),
      date: date,
      time: time
    });
    
    if (serviceIds && serviceIds.length > 0) {
      params.append('serviceIds', Array.isArray(serviceIds) ? serviceIds.join(',') : serviceIds);
    }
    
    if (excludeAppointmentId) {
      params.append('excludeAppointmentId', excludeAppointmentId.toString());
    }
    
    return this.request(`/appointments/check-conflict?${params.toString()}`);
  }

  async updateAppointment(id, data) {
    return this.request(`/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAppointment(id) {
    return this.request(`/appointments/${id}`, {
      method: 'DELETE',
    });
  }

  // Cache kontrol fonksiyonu
  _getCachedData(key) {
    const cached = cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  _setCachedData(key, data) {
    cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Cache temizleme fonksiyonu
  clearCache(key = null) {
    if (key) {
      cache.delete(key);
      console.log(`Cache cleared for key: ${key}`);
    } else {
      cache.clear();
      console.log('All cache cleared');
    }
  }

  // Servis servisleri
  async getServices() {
    const cacheKey = 'services';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached services data');
      return cached;
    }
    
    const data = await this.request('/services');
    this._setCachedData(cacheKey, data);
    return data;
  }

  async getAdminServices() {
    return this.request('/services/admin');
  }

  async getService(id) {
    return this.request(`/services/${id}`);
  }

  async createService(serviceData) {
    return this.request('/services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateService(id, serviceData) {
    return this.request(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(serviceData),
    });
  }

  async deleteService(id) {
    return this.request(`/services/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleServiceStatus(id) {
    return this.request(`/services/${id}/toggle`, {
      method: 'PUT',
    });
  }

  async toggleServicePopular(id) {
    return this.request(`/services/${id}/toggle-popular`, {
      method: 'PUT',
    });
  }

  async getPopularServices() {
    const cacheKey = 'popular-services';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached popular services data');
      return cached;
    }
    
    const data = await this.request('/services/popular');
    this._setCachedData(cacheKey, data);
    return data;
  }

  // Auth servisleri
  async login(credentials) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Galeri servisleri
  async getGalleryImages() {
    const cacheKey = 'gallery';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached gallery data');
      return cached;
    }
    
    const data = await this.request('/gallery');
    this._setCachedData(cacheKey, data);
    return data;
  }

  async uploadGalleryImage(formData) {
    return this.request('/gallery', {
      method: 'POST',
      headers: {}, // FormData için Content-Type header'ı otomatik ayarlanır
      body: formData,
    });
  }

  async deleteGalleryImage(id) {
    return this.request(`/gallery/${id}`, {
      method: 'DELETE',
    });
  }

  // Admin servisleri
  async getDashboardStats() {
    return this.request('/admin/dashboard');
  }

  async getAllAppointments() {
    // Cache bypass için timestamp ekle
    const timestamp = Date.now();
    return this.request(`/admin/appointments?t=${timestamp}`);
  }

  async updateAppointmentStatus(id, status) {
    return this.request(`/admin/appointments/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  async deleteAppointmentAdmin(id) {
    return this.request(`/admin/appointments/${id}`, {
      method: 'DELETE',
    });
  }

  async updateGalleryImage(id, data) {
    return this.request(`/gallery/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async reorderGalleryImages(items) {
    console.log('API Service - reorderGalleryImages called with:', items);
    console.log('API Service - Token exists:', !!localStorage.getItem('token'));
    return this.request('/gallery/reorder', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  // Admin login servisi
  async adminLogin(credentials) {
    return this.request('/auth/admin/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // Paket servisleri
  async getPackages() {
    const cacheKey = 'packages';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached packages data');
      return cached;
    }
    
    const data = await this.request('/packages');
    this._setCachedData(cacheKey, data);
    return data;
  }

  // Kategori servisleri
  async getCategories() {
    const cacheKey = 'categories';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached categories data');
      return cached;
    }
    
    const data = await this.request('/categories');
    this._setCachedData(cacheKey, data);
    return data;
  }

  async getAdminCategories() {
    return this.request('/categories/admin');
  }

  async createCategory(categoryData) {
    // Check if categoryData is FormData (for file upload) or regular object
    if (categoryData instanceof FormData) {
      return this.request('/categories', {
        method: 'POST',
        body: categoryData,
        headers: {
          // Don't set Content-Type for FormData
        },
      });
    } else {
      return this.request('/categories', {
        method: 'POST',
        body: JSON.stringify(categoryData),
      });
    }
  }

  async updateCategory(id, categoryData) {
    // Check if categoryData is FormData (for file upload) or regular object
    if (categoryData instanceof FormData) {
      return this.request(`/categories/${id}`, {
        method: 'PUT',
        body: categoryData,
        headers: {
          // Don't set Content-Type for FormData
        },
      });
    } else {
      return this.request(`/categories/${id}`, {
        method: 'PUT',
        body: JSON.stringify(categoryData),
      });
    }
  }

  async deleteCategory(id) {
    return this.request(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleCategoryStatus(id) {
    return this.request(`/categories/${id}/toggle`, {
      method: 'PUT',
    });
  }

  // Home Settings servisleri
  async getHomeSettings() {
    const cacheKey = 'home-settings';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached home settings data');
      return cached;
    }
    
    const data = await this.request('/home-settings');
    this._setCachedData(cacheKey, data);
    return data;
  }

  async getAdminHomeSettings() {
    return this.request('/home-settings/admin');
  }

  async updateHomeSettings(settingsData) {
    return this.request('/home-settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  async uploadHomeImage(imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    return this.request('/home-settings/upload-image', {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData
      },
    });
  }

  async getAdminPackages() {
    return this.request('/packages/admin');
  }

  async getPackage(id) {
    return this.request(`/packages/${id}`);
  }

  async createPackage(packageData) {
    return this.request('/packages', {
      method: 'POST',
      body: JSON.stringify(packageData),
    });
  }

  async updatePackage(id, packageData) {
    return this.request(`/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(packageData),
    });
  }

  async deletePackage(id) {
    return this.request(`/packages/${id}`, {
      method: 'DELETE',
    });
  }

  async togglePackageStatus(id) {
    return this.request(`/packages/${id}/toggle`, {
      method: 'PUT',
    });
  }

  // Settings servisleri
  async getSettings() {
    return this.request('/settings');
  }

  async getSetting(key) {
    return this.request(`/settings/${key}`);
  }

  async updateSettings(settings) {
    return this.request('/settings', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    });
  }

  async updateSetting(key, value, description = null, category = null) {
    return this.request(`/settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value, description, category }),
    });
  }

  async deleteSetting(key) {
    return this.request(`/settings/${key}`, {
      method: 'DELETE',
    });
  }

  // Team member servisleri
  async getTeamMembers() {
    const cacheKey = 'team-members';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached team members data');
      return cached;
    }
    
    const data = await this.request('/team');
    this._setCachedData(cacheKey, data);
    return data;
  }

  async getTeamMembersByCategory(categoryId) {
    const cacheKey = `team-members-category-${categoryId}`;
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached team members by category data');
      return cached;
    }
    
    const data = await this.request(`/team?categoryId=${categoryId}`);
    this._setCachedData(cacheKey, data);
    return data;
  }

  async getAdminTeamMembers() {
    return this.request('/team/admin');
  }

  async createTeamMember(formData) {
    return this.request('/team', {
      method: 'POST',
      headers: {
        // FormData için Content-Type header'ını kaldır
      },
      body: formData,
    });
  }

  async updateTeamMember(id, formData) {
    return this.request(`/team/${id}`, {
      method: 'PUT',
      headers: {
        // FormData için Content-Type header'ını kaldır
      },
      body: formData,
    });
  }

  async deleteTeamMember(id) {
    return this.request(`/team/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleTeamMemberActive(id) {
    return this.request(`/team/${id}/toggle-active`, {
      method: 'PUT',
    });
  }

  async getTeamMemberCategories(teamMemberId) {
    return this.request(`/team/${teamMemberId}/categories`);
  }

  async addTeamMemberCategory(teamMemberId, categoryId) {
    return this.request(`/team/${teamMemberId}/categories`, {
      method: 'POST',
      body: JSON.stringify({ categoryId }),
    });
  }

  async removeTeamMemberCategory(teamMemberId, categoryId) {
    return this.request(`/team/${teamMemberId}/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  // About Us servisleri
  async getAboutUs() {
    const cacheKey = 'about-us';
    const cached = this._getCachedData(cacheKey);
    if (cached) {
      console.log('Using cached about us data');
      return cached;
    }
    
    const data = await this.request('/about');
    this._setCachedData(cacheKey, data);
    return data;
  }

  async getAdminAboutUs() {
    return this.request('/about/admin');
  }

  async createAboutUs(formData) {
    return this.request('/about', {
      method: 'POST',
      headers: {
        // FormData için Content-Type header'ını kaldır
      },
      body: formData,
    });
  }

  async updateAboutUs(id, formData) {
    return this.request(`/about/${id}`, {
      method: 'PUT',
      headers: {
        // FormData için Content-Type header'ını kaldır
      },
      body: formData,
    });
  }

  async deleteAboutUs(id) {
    return this.request(`/about/${id}`, {
      method: 'DELETE',
    });
  }

  async toggleAboutUsActive(id) {
    return this.request(`/about/${id}/toggle-active`, {
      method: 'PUT',
    });
  }

    // Customers
  async getCustomers() {
    return this.request('/customers/admin');
  }
  
  async getCustomerCount() {
    return this.request('/customers/count');
  }
  
  async createCustomer(customerData) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }
  
  async updateCustomer(id, customerData) {
    return this.request(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }
  
  async deleteCustomer(id) {
    return this.request(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  // SMTP Settings
  async getSmtpSettings() {
    return this.request('/smtp');
  }
  
  async saveSmtpSettings(settings) {
    return this.request('/smtp', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }
  
  async updateSmtpSettings(id, settings) {
    return this.request(`/smtp/${id}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }
  
  async sendTestMail(testEmail) {
    return this.request('/smtp/test', {
      method: 'POST',
      body: JSON.stringify({ testEmail }),
    });
  }
  
  async sendCustomMail(mailData) {
    return this.request('/smtp/custom-mail', {
      method: 'POST',
      body: JSON.stringify(mailData),
    });
  }

  // Telegram Settings
  async getTelegramSettings() {
    return this.request('/telegram');
  }
  
  async saveTelegramSettings(settings) {
    return this.request('/telegram', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }
  
  async updateTelegramSettings(id, settings) {
    return this.request(`/telegram/${id}`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }
  
  async sendTelegramTestMessage() {
    return this.request('/telegram/test', {
      method: 'POST',
    });
  }

  // Password Change
  async changePassword(passwordData) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  // Surveys
  async getSurvey(token) {
    return this.request(`/surveys/${token}`);
  }
  
  async submitSurvey(token, responses) {
    return this.request(`/surveys/${token}/submit`, {
      method: 'POST',
      body: JSON.stringify({ responses }),
    });
  }
  
  async createSurvey(surveyData) {
    return this.request('/surveys/create', {
      method: 'POST',
      body: JSON.stringify(surveyData),
    });
  }
  
  async getSurveys() {
    return this.request('/surveys/admin/all');
  }
  
  async getSurveyDetail(id) {
    return this.request(`/surveys/admin/${id}`);
  }
  
  async getSurveyStats() {
    return this.request('/surveys/admin/stats');
  }
}

export default new ApiService();